"use client";

import { useEffect } from "react";
import { isProtectedPath } from "~/lib/auth/protected-routes";

/**
 * Client half of the two-layer back-button defense (Layer 2 = hard-nav logout
 * in UserMenu/MobileSidebar, which destroys the SPA and Next's router cache).
 *
 * A bfcache restore (`pageshow` with `persisted`) resumes a frozen page snapshot
 * WITHOUT remounting, so mount effects never re-run and the session state is
 * whatever was frozen. We reload on restore for two page classes:
 *
 *  • Protected pages — substitute for `Cache-Control: no-store` (which Next
 *    overrides under `cacheComponents`). The reload becomes a real request that
 *    the proxy redirects to /auth once the session is gone, so Back after
 *    sign-out can't show a frozen signed-in snapshot.
 *  • /auth — a frozen auth page can't re-run `resolveSession`, and the Supabase
 *    client's navigator lock is stuck after the freeze (so "Continue" hangs).
 *    Reloading gives a fresh client: logged out → the form works; logged in →
 *    resolveSession redirects to `next` (the page you originally came from).
 *
 * A fresh load fires `pageshow` with `persisted === false`, so this never loops.
 * Mounted once in the root layout; renders nothing.
 */
function shouldReloadOnRestore(pathname: string): boolean {
  return (
    isProtectedPath(pathname) ||
    pathname === "/auth" ||
    pathname.startsWith("/auth/")
  );
}

export function ProtectedBackGuard() {
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted && shouldReloadOnRestore(window.location.pathname)) {
        window.location.reload();
      }
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  return null;
}
